import React, { useState } from "react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/ui/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Barcode, Calendar } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";

const initialProducts = [
  {
    id: "9999865702000341",
    title: "Advocate Glucose SOS Sachet 15g",
    brand: "Glucose SOS",
    servingSize: 0,
    upc: "865702000341",
    imageUrl: "https://images.unsplash.com/photo-1535268647677-300dbf3d78d1",
    dateAdded: "2023-09-15",
    testResults: 42
  },
  {
    id: "9999865702000404",
    title: "Advocate Glucose SOS Sachet 15g",
    brand: "Glucose SOS",
    servingSize: 0,
    upc: "867469000404",
    imageUrl: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
    dateAdded: "2023-10-22",
    testResults: 28
  },
  {
    id: "9998500113036996",
    title: "Collagen GlucoTrojan",
    brand: "JBA",
    servingSize: 0,
    upc: "850011130636",
    imageUrl: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07",
    dateAdded: "2023-11-05",
    testResults: 36
  },
  {
    id: "909124814496790",
    title: "Crispy Treats, Chocolate Chip",
    brand: "Blake's Seed Based",
    servingSize: 0,
    upc: "857925008365",
    imageUrl: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
    dateAdded: "2023-08-30",
    testResults: 18
  },
  {
    id: "970104463003224",
    title: "Dr. Fuhrman's Lemon Cookie G-Bombs Nutrition Bars",
    brand: "DrFuhrman",
    servingSize: 0,
    upc: "829584300210",
    imageUrl: "https://images.unsplash.com/photo-1535268647677-300dbf3d78d1",
    dateAdded: "2023-07-12",
    testResults: 24
  }
];

const Products: React.FC = () => {
  const [products, setProducts] = useState(initialProducts);
  const [newProduct, setNewProduct] = useState({
    id: "",
    title: "",
    brand: "",
    servingSize: 0,
    upc: "",
    imageUrl: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
    dateAdded: new Date().toISOString().split('T')[0],
    testResults: 0
  });
  
  const [showAddForm, setShowAddForm] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewProduct({
      ...newProduct,
      [name]: value
    });
  };

  const handleAddProduct = () => {
    if (newProduct.title && newProduct.brand && newProduct.upc) {
      setProducts([...products, newProduct]);
      setNewProduct({
        id: "",
        title: "",
        brand: "",
        servingSize: 0,
        upc: "",
        imageUrl: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
        dateAdded: new Date().toISOString().split('T')[0],
        testResults: 0
      });
      setShowAddForm(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">View and manage study products</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">Export Data</Button>
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {showAddForm && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Add New Product</CardTitle>
            <CardDescription>Enter the details for the new product</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Title</label>
                <Input 
                  name="title"
                  value={newProduct.title}
                  onChange={handleInputChange}
                  placeholder="Product title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Brand</label>
                <Input 
                  name="brand"
                  value={newProduct.brand}
                  onChange={handleInputChange}
                  placeholder="Brand name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">UPC</label>
                <Input 
                  name="upc"
                  value={newProduct.upc}
                  onChange={handleInputChange}
                  placeholder="UPC/Barcode"
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
                <Button onClick={handleAddProduct}>Save Product</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Brand Owner</TableHead>
                <TableHead>Serving Size</TableHead>
                <TableHead>GTIN UPC</TableHead>
                <TableHead>Added Date</TableHead>
                <TableHead>Test Results</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} className="hover:bg-muted/50 cursor-pointer">
                  <TableCell>
                    <div className="h-12 w-12 rounded-md overflow-hidden">
                      <img 
                        src={product.imageUrl} 
                        alt={product.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </TableCell>
                  <TableCell>{product.title}</TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>{product.servingSize}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Barcode className="h-4 w-4 mr-2 text-gray-500" />
                      {product.upc}
                    </div>
                  </TableCell>
                  <TableCell>{product.dateAdded}</TableCell>
                  <TableCell>
                    <div className="bg-blue-100 text-blue-800 py-1 px-2 rounded-full text-xs font-medium inline-block">
                      {product.testResults}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Products;
